import React, { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";

/* Aceternity-style Terminal — types commands, prints outputs, blinks cursor.
   Starts when scrolled into view; runs once. */
export const Terminal = ({ commands = [], outputs = {}, typingSpeed = 45, delayBetweenCommands = 1000, className, title = "bookingflows — zsh" }) => {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const [lines, setLines] = useState([]);
  const [typing, setTyping] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          io.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let cancelled = false;
    const timers = [];
    const wait = (ms) => new Promise((res) => timers.push(setTimeout(res, ms)));

    (async () => {
      for (let i = 0; i < commands.length; i++) {
        const cmd = commands[i];
        for (let c = 1; c <= cmd.length; c++) {
          if (cancelled) return;
          setTyping(cmd.slice(0, c));
          await wait(typingSpeed);
        }
        if (cancelled) return;
        setTyping("");
        setLines((prev) => [...prev, { type: "cmd", text: cmd }]);
        for (const out of outputs[i] || []) {
          await wait(220);
          if (cancelled) return;
          setLines((prev) => [...prev, { type: "out", text: out }]);
        }
        await wait(delayBetweenCommands);
      }
      if (!cancelled) setDone(true);
    })();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [started]); // eslint-disable-line

  return (
    <div ref={ref} className={cn("overflow-hidden rounded-2xl bg-gray-950 shadow-2xl text-left", className)} data-testid="embed-terminal">
      <div className="flex items-center gap-2 px-4 py-3 bg-white/5">
        <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
        <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
        <span className="w-3 h-3 rounded-full bg-[#28C840]" />
        <span className="ml-3 text-[11px] text-gray-500 font-medium tracking-wide">{title}</span>
      </div>
      <div className="px-5 py-4 font-mono text-[13px] leading-7 min-h-[220px]" dir="ltr">
        {lines.map((l, i) =>
          l.type === "cmd" ? (
            <div key={i} className="text-gray-100">
              <span className="text-blue-400">$ </span>
              {l.text}
            </div>
          ) : (
            <div key={i} className="text-emerald-400/90">{l.text}</div>
          )
        )}
        {!done && (
          <div className="text-gray-100">
            <span className="text-blue-400">$ </span>
            {typing}
            <span className="inline-block w-2 h-4 align-middle bg-gray-300 animate-pulse ml-0.5" />
          </div>
        )}
        {done && (
          <div className="text-gray-100">
            <span className="text-blue-400">$ </span>
            <span className="inline-block w-2 h-4 align-middle bg-gray-300 animate-pulse" />
          </div>
        )}
      </div>
    </div>
  );
};
